import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award, Bell, Calendar, Car, ClipboardCheck, CreditCard, FileText, KeyRound, Loader2, LogOut, MessageCircle, User } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { usePresenceHeartbeat } from '../hooks/usePresenceHeartbeat';
import type { CandidateProfile } from '../types';
import {
  fetchConversations,
  fetchDocumentRequests,
  fetchLessons,
  fetchPayableInvoices,
  fetchSlots,
  MESSAGING_REFRESH_MS,
  setPassword,
} from '../api/candidate';
import {
  disablePush,
  enablePush,
  getPushState,
  PushState,
} from '../api/push';
import {
  getWebauthnStatus,
  registerPasskey,
  webauthnSupported,
} from '../api/webauthn';
import { Fingerprint } from 'lucide-react';
import { initials } from '../lib/dates';
import { MyPhotoEditor, Photo, useMyPhotos } from './Photos';
import { slotLabel } from '../lib/format';
import DashboardView from '../views/DashboardView';
import ReserveView from '../views/ReserveView';
import CommandView from '../views/CommandView';
import PaymentsView from '../views/PaymentsView';
import DocumentsView from '../views/DocumentsView';
import LivretView from '../views/LivretView';
import EvaluationView from '../views/EvaluationView';
import PermisView from '../views/PermisView';
import MessagesView from '../views/MessagesView';
import InstallAppBanner from './InstallAppBanner';
import { fetchLicenceFollowup, fetchStartingEvaluation } from '../api/candidate';

type Tab =
  | 'dashboard'
  | 'reserver'
  | 'commander'
  | 'paiements'
  | 'documents'
  | 'livret'
  | 'messages'
  | 'evaluation'
  | 'permis';

const BASE_TABS: { id: Tab; label: string }[] = [
  { id: 'dashboard', label: 'Tableau de bord' },
  { id: 'messages', label: 'Messages' },
  { id: 'reserver', label: 'Réserver une leçon' },
  { id: 'commander', label: 'Commander' },
  { id: 'paiements', label: 'Mes paiements' },
  { id: 'documents', label: 'Mes documents' },
  { id: 'livret', label: 'Mon livret' },
];
const EVALUATION_TAB: { id: Tab; label: string } = {
  id: 'evaluation',
  label: 'Évaluation de départ',
};
const PERMIS_TAB: { id: Tab; label: string } = {
  id: 'permis',
  label: 'Mon permis',
};

/** Libellé d'un onglet ; « Messages » porte un pictogramme et le nombre de non-lus. */
function TabLabel({
  tab,
  unreadMessages,
}: {
  tab: { id: Tab; label: string };
  unreadMessages: number;
}) {
  if (tab.id !== 'messages') return <>{tab.label}</>;
  return (
    <span className="relative inline-flex items-center gap-1.5">
      <MessageCircle size={16} aria-hidden="true" />
      {tab.label}
      {unreadMessages > 0 && (
        <span className="min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          <span aria-hidden="true">
            {unreadMessages > 99 ? '99+' : unreadMessages}
          </span>
          <span className="sr-only">
            {` ${unreadMessages} non lu${unreadMessages > 1 ? 's' : ''}`}
          </span>
        </span>
      )}
    </span>
  );
}

export default function Layout() {
  const candidate = useAuthStore((s) => s.candidate);
  const setCandidate = useAuthStore((s) => s.setCandidate);
  const logout = useAuthStore((s) => s.logout);
  usePresenceHeartbeat(!!candidate);
  const [tab, setTab] = useState<Tab>(() => {
    const seg = window.location.pathname.replace(/^\/+/, '');
    const valid = [...BASE_TABS.map((t) => t.id), EVALUATION_TAB.id, PERMIS_TAB.id];
    return (valid.includes(seg as Tab) ? (seg as Tab) : 'dashboard');
  });

  // Évaluation de départ : l'onglet n'apparaît que si l'auto-école en a demandé
  // une (à réaliser ou déjà réalisée).
  const evaluationQuery = useQuery({
    queryKey: ['candidate', 'starting-evaluation'],
    queryFn: fetchStartingEvaluation,
    staleTime: 60 * 1000,
  });
  const evalStatus = evaluationQuery.data?.status ?? 'not_requested';
  // Mon permis : l'onglet n'apparaît qu'une fois le parcours « permis obtenu » lancé par
  // l'auto-école ; une erreur de lecture le laisse caché.
  const licenceQuery = useQuery({
    queryKey: ['candidate', 'licence-followup'],
    queryFn: fetchLicenceFollowup,
    staleTime: 60 * 1000,
  });
  const licenceFollowup = licenceQuery.data ?? null;
  const evalPending = evalStatus === 'pending';
  // « Mes paiements » masqué si l'auto-école ne l'autorise pas (Accès Candidat).
  const canViewPayments = candidate?.access?.canViewPayments !== false;
  const baseTabs = canViewPayments
    ? BASE_TABS
    : BASE_TABS.filter((t) => t.id !== 'paiements');
  const TABS = [
    ...baseTabs,
    ...(evalStatus === 'not_requested' ? [] : [EVALUATION_TAB]),
    ...(licenceFollowup ? [PERMIS_TAB] : []),
  ];
  // Avec « Messages », les onglets ne tiennent sur la ligne du logo que si
  // l'en-tête a toute sa largeur (1152 px), sans l'onglet d'évaluation ni
  // « Mon permis » ; sinon ils passent sur la rangée défilante.
  const tabsFitTopBar = evalStatus === 'not_requested' && !licenceFollowup;
  // Adresse /permis sans parcours (lien ancien, parcours absent) : retour au tableau de bord.
  useEffect(() => {
    if (tab === 'permis' && !licenceQuery.isPending && !licenceFollowup) setTab('dashboard');
  }, [tab, licenceQuery.isPending, licenceFollowup]);
  const [profileOpen, setProfileOpen] = useState(false);

  // Reflète l'onglet courant dans l'URL (évite de rester bloqué sur /login).
  useEffect(() => {
    const path = tab === 'dashboard' ? '/' : `/${tab}`;
    if (window.location.pathname !== path) {
      window.history.replaceState({}, '', path);
    }
  }, [tab]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pushState, setPushState] = useState<PushState>('default');
  const [pushBusy, setPushBusy] = useState(false);
  // La permission navigateur est-elle déjà accordée ? (évite de re-proposer
  // « Activer » quand l'abonnement local n'a pas encore été rétabli.)
  const notifAlreadyGranted =
    typeof Notification !== 'undefined' && Notification.permission === 'granted';
  const [readNotifIds, setReadNotifIds] = useState<string[]>([]);

  // Notifications lues : mémorisées par candidat dans le navigateur.
  const readKey = candidate ? `pmd-candidat-notif-read:${candidate.id}` : null;
  useEffect(() => {
    if (!readKey) return;
    try {
      const raw = localStorage.getItem(readKey);
      setReadNotifIds(raw ? (JSON.parse(raw) as string[]) : []);
    } catch {
      setReadNotifIds([]);
    }
  }, [readKey]);
  useEffect(() => {
    if (!readKey) return;
    try {
      localStorage.setItem(readKey, JSON.stringify(readNotifIds));
    } catch {
      // stockage indisponible : ignoré.
    }
  }, [readKey, readNotifIds]);

  useEffect(() => {
    getPushState()
      .then(async (st) => {
        // Permission déjà accordée mais abonnement local manquant (SW réinstallé,
        // etc.) : on ré-abonne silencieusement pour ne pas re-demander l'activation.
        if (
          st === 'default' &&
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted'
        ) {
          try {
            setPushState(await enablePush());
            return;
          } catch {
            // échec du ré-abonnement : on garde l'état courant.
          }
        }
        setPushState(st);
      })
      .catch(() => undefined);
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      setPushState(
        pushState === 'subscribed' ? await disablePush() : await enablePush(),
      );
    } catch {
      // silencieux : l'utilisateur peut réessayer.
    } finally {
      setPushBusy(false);
    }
  };

  const [bioRegistered, setBioRegistered] = useState(false);
  const [bioBusy, setBioBusy] = useState(false);
  const bioSupported = webauthnSupported();

  useEffect(() => {
    if (!bioSupported) return;
    getWebauthnStatus()
      .then(setBioRegistered)
      .catch(() => undefined);
  }, [bioSupported]);

  const enableBiometric = async () => {
    setBioBusy(true);
    try {
      await registerPasskey();
      setBioRegistered(true);
    } catch {
      // annulé ou non supporté : on ne change rien.
    } finally {
      setBioBusy(false);
    }
  };
  const menuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen && !notifOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen, notifOpen]);

  // Notifications dérivées des vraies données (prochaine leçon, créneaux ouverts).
  const lessonsQuery = useQuery({
    queryKey: ['candidate', 'lessons'],
    queryFn: fetchLessons,
  });
  const slotsQuery = useQuery({
    queryKey: ['candidate', 'slots'],
    queryFn: fetchSlots,
  });
  const docRequestsQuery = useQuery({
    queryKey: ['candidate', 'document-requests'],
    queryFn: fetchDocumentRequests,
  });
  const payableQuery = useQuery({
    queryKey: ['candidate', 'payable-invoices'],
    queryFn: fetchPayableInvoices,
  });
  // Messages non lus : compteur de l'onglet « Messages », relu périodiquement.
  const conversationsQuery = useQuery({
    queryKey: ['candidate', 'messaging', 'conversations'],
    queryFn: fetchConversations,
    refetchInterval: MESSAGING_REFRESH_MS,
    refetchOnWindowFocus: true,
  });
  const unreadMessages = (conversationsQuery.data ?? []).reduce(
    (total, c) => total + c.unread,
    0,
  );
  // Première leçon non terminée (la liste inclut aussi les leçons passées).
  const nextLesson =
    (lessonsQuery.data ?? []).find(
      (l) => new Date(l.endsAt).getTime() >= Date.now(),
    ) ?? null;
  const openSlots = slotsQuery.data?.length ?? 0;
  const pendingDocs = (docRequestsQuery.data ?? []).filter(
    (r) => r.status === 'requested',
  ).length;
  const payableInvoices = payableQuery.data?.paymentEnabled
    ? (payableQuery.data?.invoices.length ?? 0)
    : 0;
  const notifications: {
    id: string;
    icon: JSX.Element;
    title: string;
    desc: string;
    /** Onglet vers lequel rediriger au clic sur la notification. */
    tab?: Tab;
  }[] = [];
  if (payableInvoices > 0) {
    notifications.push({
      id: `invoices:${payableInvoices}`,
      icon: <CreditCard size={14} className="text-indigo-600" />,
      title: `${payableInvoices} facture${payableInvoices > 1 ? 's' : ''} à régler`,
      desc: 'Réglez vos factures en ligne depuis « Mes paiements ».',
      tab: 'paiements',
    });
  }
  if (pendingDocs > 0) {
    notifications.push({
      id: `docs:${pendingDocs}`,
      icon: <FileText size={14} className="text-amber-600" />,
      title: `${pendingDocs} document${pendingDocs > 1 ? 's' : ''} à fournir`,
      desc: 'Votre auto-école vous réclame des pièces. Rendez-vous dans « Mes documents ».',
      tab: 'documents',
    });
  }
  if (nextLesson) {
    notifications.push({
      id: `lesson:${nextLesson.startsAt}`,
      icon: <Car size={14} className="text-indigo-600" />,
      title: 'Rappel : votre prochaine leçon',
      desc: slotLabel(nextLesson.startsAt, nextLesson.endsAt),
      tab: 'dashboard',
    });
  }
  if (openSlots > 0) {
    notifications.push({
      id: `slots:${openSlots}`,
      icon: <Calendar size={14} className="text-green-600" />,
      title: `${openSlots} créneau${openSlots > 1 ? 'x' : ''} disponible${
        openSlots > 1 ? 's' : ''
      }`,
      desc: 'Réservez votre prochaine leçon en un clic.',
      tab: 'reserver',
    });
  }

  const unread = notifications.filter((n) => !readNotifIds.includes(n.id));
  const markAllRead = () => {
    const ids = notifications.map((n) => n.id);
    setReadNotifIds((prev) => Array.from(new Set([...prev, ...ids])));
  };
  const markRead = (notifId: string) =>
    setReadNotifIds((prev) => (prev.includes(notifId) ? prev : [...prev, notifId]));

  const fullName = candidate
    ? `${candidate.firstName} ${candidate.lastName}`
    : '';
  const monogram = candidate
    ? initials(candidate.firstName, candidate.lastName)
    : '··';
  const myPhotos = useMyPhotos();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Navbar ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 safe-top">
        <div className="max-w-6xl mx-auto px-6">
          <div className="h-16 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/logo-permio.png"
                alt="Permio"
                className="h-7 w-auto flex-shrink-0"
              />
            </div>

            <nav
              className={`hidden items-center gap-5 h-16 flex-shrink-0 ${
                tabsFitTopBar ? 'min-[1152px]:flex' : ''
              }`}
            >
              {TABS.map((t) => {
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`h-16 whitespace-nowrap border-b-2 text-sm font-medium transition-colors px-1 ${
                      active
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <TabLabel tab={t} unreadMessages={unreadMessages} />
                  </button>
                );
              })}
            </nav>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotifOpen((v) => !v)}
                  className="relative p-2.5 rounded-lg hover:bg-slate-100 transition-colors"
                  aria-label="Notifications"
                >
                  <Bell size={20} className="text-slate-600" />
                  {unread.length > 0 && (
                    <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {unread.length}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-in">
                    <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-900 text-sm">
                        Notifications
                      </h3>
                      {unread.length > 0 && (
                        <button
                          onClick={markAllRead}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                        >
                          Tout marquer comme lu
                        </button>
                      )}
                    </div>

                    {/* Invitation à activer les notifications push */}
                    {pushState !== 'unsupported' && pushState !== 'subscribed' && !notifAlreadyGranted && (
                      <div className="px-4 py-3 border-b border-slate-100 bg-indigo-50/60">
                        {pushState === 'denied' ? (
                          <p className="text-xs text-slate-600">
                            Notifications bloquées. Autorisez-les pour Permio dans les
                            réglages de votre navigateur ou de votre téléphone.
                          </p>
                        ) : (
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs text-slate-700 flex-1">
                              Activez les notifications pour être prévenu·e.
                            </p>
                            <button
                              onClick={togglePush}
                              disabled={pushBusy}
                              className="flex-shrink-0 px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                            >
                              {pushBusy ? '…' : 'Activer'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {notifications.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-slate-500">
                        Aucune notification pour le moment.
                      </div>
                    ) : (
                      notifications.map((n) => {
                        const isUnread = !readNotifIds.includes(n.id);
                        return (
                          <button
                            key={n.id}
                            type="button"
                            onClick={() => {
                              markRead(n.id);
                              if (n.tab) {
                                setTab(n.tab);
                                setNotifOpen(false);
                              }
                            }}
                            className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 transition-colors ${
                              isUnread ? 'bg-indigo-50/40 hover:bg-indigo-50' : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="p-2 rounded-lg flex-shrink-0 bg-slate-100 relative">
                                {n.icon}
                                {isUnread && (
                                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-600 rounded-full" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${isUnread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                                  {n.title}
                                </p>
                                <p className="text-xs text-slate-600 leading-relaxed">
                                  {n.desc}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  className="relative overflow-hidden w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold hover:ring-2 hover:ring-indigo-200 transition-all"
                  aria-label="Mon compte"
                >
                  {monogram}
                  <Photo url={myPhotos.me} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-in">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {fullName}
                      </div>
                      {candidate?.email && (
                        <div className="text-xs text-slate-500 truncate">
                          {candidate.email}
                        </div>
                      )}
                    </div>
                    <div className="p-1.5">
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          setProfileOpen(true);
                        }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors text-left"
                      >
                        <User size={15} className="text-slate-500" />
                        Mon profil
                      </button>
                      {pushState !== 'unsupported' && (
                        <button
                          onClick={togglePush}
                          disabled={pushBusy || pushState === 'denied'}
                          className="w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors text-left"
                        >
                          <span className="flex items-center gap-2.5">
                            <Bell size={15} className="text-slate-500" />
                            Notifications
                          </span>
                          <span
                            className={`text-xs font-semibold ${
                              pushState === 'subscribed'
                                ? 'text-green-600'
                                : pushState === 'denied'
                                  ? 'text-red-500'
                                  : 'text-indigo-600'
                            }`}
                          >
                            {pushState === 'subscribed'
                              ? 'Activées'
                              : pushState === 'denied'
                                ? 'Bloquées'
                                : 'Activer'}
                          </span>
                        </button>
                      )}
                      {bioSupported && (
                        <button
                          onClick={enableBiometric}
                          disabled={bioBusy || bioRegistered}
                          className="w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-70 transition-colors text-left"
                        >
                          <span className="flex items-center gap-2.5">
                            <Fingerprint size={15} className="text-slate-500" />
                            FaceID / Touch ID
                          </span>
                          <span
                            className={`text-xs font-semibold ${
                              bioRegistered ? 'text-green-600' : 'text-indigo-600'
                            }`}
                          >
                            {bioRegistered ? 'Activé' : 'Activer'}
                          </span>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setMenuOpen(false);
                          setPwdOpen(true);
                        }}
                        className="w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors text-left"
                      >
                        <span className="flex items-center gap-2.5">
                          <KeyRound size={15} className="text-slate-500" />
                          Mot de passe
                        </span>
                        <span className="text-xs font-semibold text-indigo-600">
                          {candidate?.hasPassword ? 'Changer' : 'Définir'}
                        </span>
                      </button>
                      <button
                        onClick={() => logout()}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors text-left"
                      >
                        <LogOut size={15} />
                        Se déconnecter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Onglets mobile / tablette */}
          <nav
            className={`flex items-center gap-1 overflow-x-auto pb-2 -mb-px ${
              tabsFitTopBar ? 'min-[1152px]:hidden' : ''
            }`}
          >
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <TabLabel tab={t} unreadMessages={unreadMessages} />
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <InstallAppBanner />

      {/* Rappel : évaluation de départ à réaliser */}
      {evalPending && tab !== 'evaluation' && (
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <button
            type="button"
            onClick={() => setTab('evaluation')}
            className="w-full flex items-center gap-3 text-left bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 hover:bg-indigo-100 transition-colors"
          >
            <span className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center flex-shrink-0">
              <ClipboardCheck size={18} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-slate-900">
                Votre évaluation de départ vous attend
              </span>
              <span className="block text-xs text-slate-600">
                Quelques minutes pour aider votre auto-école à personnaliser
                votre formation.
              </span>
            </span>
            <span className="text-sm font-semibold text-indigo-700 flex-shrink-0">
              Commencer →
            </span>
          </button>
        </div>
      )}

      {/* Permis obtenu : félicitations, pièces et questionnaire en attente */}
      {licenceFollowup && !licenceFollowup.survey.answeredAt && tab !== 'permis' && (
        <div className="max-w-6xl mx-auto px-6 pt-6">
          <button
            type="button"
            onClick={() => setTab('permis')}
            className="w-full flex items-center gap-3 text-left bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 hover:bg-indigo-100 transition-colors"
          >
            <span className="w-9 h-9 rounded-lg bg-indigo-600 text-white flex items-center justify-center flex-shrink-0">
              <Award size={18} aria-hidden="true" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-slate-900">
                Félicitations pour votre permis !
              </span>
              <span className="block text-xs text-slate-600">
                Déposez les pièces pour la fabrication de votre permis et
                donnez-nous votre avis.
              </span>
            </span>
            <span className="text-sm font-semibold text-indigo-700 flex-shrink-0">
              Voir →
            </span>
          </button>
        </div>
      )}

      {/* ── Contenu ────────────────────────────────────────────────────── */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {tab === 'dashboard' && (
          <DashboardView onReserve={() => setTab('reserver')} />
        )}
        {tab === 'reserver' && <ReserveView />}
        {tab === 'commander' && (
          <CommandView />
        )}
        {tab === 'paiements' && <PaymentsView />}
        {tab === 'documents' && <DocumentsView />}
        {tab === 'livret' && <LivretView />}
        {tab === 'messages' && <MessagesView />}
        {tab === 'evaluation' && <EvaluationView />}
        {tab === 'permis' && licenceFollowup && (
          <PermisView followup={licenceFollowup} onOpenDocuments={() => setTab('documents')} />
        )}
      </main>

      {pwdOpen && (
        <PasswordModal
          hasPassword={!!candidate?.hasPassword}
          onClose={() => setPwdOpen(false)}
          onSaved={() => {
            if (candidate) setCandidate({ ...candidate, hasPassword: true });
            setPwdOpen(false);
          }}
        />
      )}

      {profileOpen && candidate && (
        <ProfileModal candidate={candidate} onClose={() => setProfileOpen(false)} />
      )}
    </div>
  );
}

function ProfileModal({
  candidate,
  onClose,
}: {
  candidate: CandidateProfile;
  onClose: () => void;
}) {
  const rows: { label: string; value: string | null | undefined }[] = [
    { label: 'Nom', value: `${candidate.firstName} ${candidate.lastName}`.trim() },
    { label: 'Email', value: candidate.email },
    { label: 'Téléphone', value: candidate.phoneNumber },
    { label: 'Catégorie de permis', value: candidate.vehicleType },
    { label: 'Auto-école', value: candidate.school?.name },
  ];

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900">Mon profil</h3>
          <button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-600">
            ✕
          </button>
        </div>
        <MyPhotoEditor initials={initials(candidate.firstName, candidate.lastName)} />
        <dl className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.label} className="py-2.5 flex items-center justify-between gap-4">
              <dt className="text-sm text-slate-500 flex-shrink-0">{r.label}</dt>
              <dd className="text-sm font-medium text-slate-900 text-right min-w-0 truncate">
                {r.value || '—'}
              </dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-slate-400 mt-4">
          Pour modifier ces informations, contactez votre auto-école.
        </p>
        <button
          onClick={onClose}
          className="mt-4 w-full py-2.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}

function PasswordModal({
  hasPassword,
  onClose,
  onSaved,
}: {
  hasPassword: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (next.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (next !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setBusy(true);
    try {
      await setPassword(next, hasPassword ? current : undefined);
      onSaved();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'Échec de la mise à jour.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
        <h2 className="text-slate-900 mb-1">
          {hasPassword ? 'Changer mon mot de passe' : 'Définir un mot de passe'}
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Vous pourrez ensuite vous connecter avec votre email et ce mot de
          passe.
        </p>
        <form onSubmit={submit} className="space-y-3">
          {hasPassword && (
            <input
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="Mot de passe actuel"
              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-slate-900"
            />
          )}
          <input
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="Nouveau mot de passe (8 caractères min.)"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-slate-900"
          />
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirmer le mot de passe"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none text-slate-900"
          />
          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
